/**
 * PostHog serves one region per project from three different hostnames, and the
 * region is fixed when the project is created. Only the ingest host is
 * configured (`NEXT_PUBLIC_POSTHOG_HOST`); the other two are derived, so moving
 * regions — or pointing at a self-hosted instance — is a one-value change.
 *
 * Every function here accepts `undefined` and applies the default itself.
 * `env.mjs` declares the same default, but `skipValidation` is on for CI and
 * preview builds, which turns `env` into a bare passthrough over `process.env`
 * with no Zod defaults applied — so a caller reading `env.NEXT_PUBLIC_POSTHOG_HOST`
 * genuinely can get `undefined` there.
 *
 * Plain `.mjs`, like `env.mjs` beside it. Types are inferred from the JSDoc and
 * `allowJs` is on, which is how both are imported from TypeScript.
 */

/** Matches `https://<region>.i.posthog.com`, capturing the scheme and region. */
const REGION_INGEST_HOST = /^(https?:\/\/)([a-z0-9-]+)\.i\.posthog\.com$/

export const DEFAULT_POSTHOG_INGEST_HOST = "https://eu.i.posthog.com"

/**
 * Where events are sent. Note the browser does not talk to this directly — it
 * posts to the same-origin `/ingest` proxy, which `next.config.mjs` rewrites
 * here.
 *
 * @param {string | undefined} [configuredHost] `NEXT_PUBLIC_POSTHOG_HOST`.
 * @returns {string}
 */
export function posthogIngestHostFor(configuredHost) {
  const host = configuredHost || DEFAULT_POSTHOG_INGEST_HOST
  return host.endsWith("/") ? host.slice(0, -1) : host
}

/**
 * Where posthog-js fetches its bundles (recorder, surveys, toolbar) from.
 *
 * Cloud regions serve static assets from a separate `-assets` host. A
 * self-hosted instance serves everything from one origin, so an unrecognised
 * host is returned unchanged rather than having `-assets` spliced into it.
 *
 * @param {string | undefined} [configuredHost] `NEXT_PUBLIC_POSTHOG_HOST`.
 * @returns {string}
 */
export function posthogAssetsHostFor(configuredHost) {
  return posthogIngestHostFor(configuredHost).replace(
    REGION_INGEST_HOST,
    "$1$2-assets.i.posthog.com",
  )
}

/**
 * Where the PostHog app itself lives — the target of toolbar and "view in
 * PostHog" links. posthog-js cannot infer it here because `api_host` is the
 * same-origin `/ingest` proxy rather than a posthog.com URL.
 *
 * @param {string | undefined} [configuredHost] `NEXT_PUBLIC_POSTHOG_HOST`.
 * @returns {string}
 */
export function posthogUiHostFor(configuredHost) {
  return posthogIngestHostFor(configuredHost).replace(
    REGION_INGEST_HOST,
    "$1$2.posthog.com",
  )
}
